import React from 'react';
import '@atlaskit/css-reset';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { NotFoundErrorPageAsync } from '@atlassian/jira-error-pages/src/async.tsx';
import type FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import {
	JiraProjectVersionExperienceStart,
	useJiraProjectVersionExperienceFail,
	JiraProjectVersionExperienceExpectedFailureSuccess,
} from '@atlassian/jira-project-versions/src/services/experience-tracker/JiraProjectVersionExperienceExpectedFailureSuccess.tsx';
import {
	RedirectToArchivedPage,
	isProjectArchivedCondition,
} from '@atlassian/jira-redirect-archived-project/src/ui/index.tsx';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { withRouter, type RouteContext, useResource } from '@atlassian/react-resource-router';
import { Skeleton } from './skeleton/index.tsx';
import RelayVersions from './view/index.tsx';

export type Props = RouteContext;

const RelayProjectVersions = ({ match }: Props) => {
	const projectContext = useResource(projectContextResource);
	const projectKey = match.params.projectKey ?? '';

	const onExperienceFail = useJiraProjectVersionExperienceFail();

	const isNonExistingProjectKey =
		projectContext.data == null &&
		projectContext.error != null &&
		// Casting to `FetchError` because if the resource was fetched during SSR, the serialisation & deserialisation
		// process will have converted it into an `Error` (even though it still has the `statusCode` property).
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		(projectContext.error as FetchError).statusCode === 404;

	if (isProjectArchivedCondition(projectContext.data)) {
		return <RedirectToArchivedPage />;
	}

	return (
		<UFOSegment name="project-versions">
			<JiraProjectVersionExperienceStart experienceId={projectKey} />
			<AuthenticationCheck>
				{isNonExistingProjectKey ? (
					<>
						<NotFoundErrorPageAsync />
						<JiraProjectVersionExperienceExpectedFailureSuccess reason="Project does not exist with the given project key" />
					</>
				) : (
					<Placeholder name="project-versions" fallback={<Skeleton />}>
						{projectKey ? (
							<JSErrorBoundary
								id="projectVersions"
								packageName="jiraSpaAppsProjectVersions"
								teamName="jolt"
								onError={(loc: string, error: Error) => {
									onExperienceFail(loc, error);
								}}
							>
								<RelayVersions projectKey={projectKey} />
							</JSErrorBoundary>
						) : null}
					</Placeholder>
				)}
			</AuthenticationCheck>
		</UFOSegment>
	);
};

export default withRouter(RelayProjectVersions);
